/**
 * Chat Page - Thread List & Chat View
 * Displays chat threads and messages for buyer's orders
 */

import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useChatStore } from '@/store/chat';
import { MessageCircle, ArrowLeft, Package } from 'lucide-react';
import { MessageBubble } from '@/components/chat/MessageBubble';
import { TypingIndicator } from '@/components/chat/TypingIndicator';
import { MessageInput } from '@/components/chat/MessageInput';
import { formatDistance } from 'date-fns';

export default function ChatPage() {
  const navigate = useNavigate();
  const { threadId } = useParams<{ threadId?: string }>();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const {
    threads,
    isLoadingThreads,
    currentThreadId,
    messages,
    isLoadingMessages,
    typingUsers,
    fetchThreads,
    selectThread,
    sendMessage,
    setTyping,
  } = useChatStore();

  // Fetch threads on mount
  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  // Select thread from URL
  useEffect(() => {
    if (threadId && threadId !== currentThreadId) {
      selectThread(threadId);
    }
  }, [threadId, currentThreadId, selectThread]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleThreadClick = (tid: string) => {
    navigate(`/chat/${tid}`);
  };

  const handleSendMessage = async (message: string) => {
    if (currentThreadId) {
      await sendMessage(currentThreadId, message);
    }
  };

  const handleTyping = (isTyping: boolean) => {
    if (currentThreadId) {
      setTyping(currentThreadId, isTyping);
    }
  };

  // Show thread list if no thread selected
  if (!threadId) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MessageCircle className="w-7 h-7 text-green-600" />
            Messages
          </h1>
          <p className="text-gray-600 mt-1">Chat with vendors and riders about your orders</p>
        </div>

        {isLoadingThreads ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          </div>
        ) : threads.length === 0 ? (
          <div className="text-center py-24">
            <MessageCircle className="w-24 h-24 text-gray-300 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-700 mb-2">No messages yet</h2>
            <p className="text-gray-500 mb-8">
              Place an order to start chatting with vendors and riders
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-8 rounded-lg transition"
            >
              Browse Vendors
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {threads.map((thread) => (
              <button
                key={thread.thread_id}
                onClick={() => handleThreadClick(thread.thread_id)}
                className="w-full bg-white border border-gray-200 rounded-lg p-4 hover:border-green-500 transition text-left"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-gray-400" />
                    <span className="font-semibold text-gray-900">
                      Order {thread.order_id.substring(0, 8)}
                    </span>
                  </div>
                  {thread.unread_count > 0 && (
                    <span className="bg-green-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                      {thread.unread_count}
                    </span>
                  )}
                </div>
                
                {thread.last_message && (
                  <div>
                    <p className="text-sm text-gray-600 truncate">
                      <span className="font-medium">
                        {thread.last_message.sender_role === 'buyer' ? 'You' : thread.last_message.sender_role}:
                      </span>{' '}
                      {thread.last_message.message}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatDistance(new Date(thread.last_message.created_at), new Date(), {
                        addSuffix: true,
                      })}
                    </p>
                  </div>
                )}

                {thread.closed_at && (
                  <div className="mt-2">
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                      Thread closed
                    </span>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Show chat thread view
  const currentThread = threads.find((t) => t.thread_id === threadId);
  const typingUserRole = typingUsers.get(threadId);

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-200 p-4 bg-white">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/chat')}
            className="p-2 hover:bg-gray-100 rounded-full transition"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h2 className="font-bold text-lg">
              Order {currentThread?.order_id.substring(0, 8) || 'Chat'}
            </h2>
            <p className="text-sm text-gray-500">
              Chat with vendor and rider
            </p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoadingMessages ? (
          <div className="flex justify-center items-center h-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <MessageCircle className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-gray-500">No messages yet. Start the conversation!</p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                isOwnMessage={message.sender_role === 'buyer'}
              />
            ))}
            {typingUserRole && <TypingIndicator userRole={typingUserRole} />}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <MessageInput
        onSend={handleSendMessage}
        onTyping={handleTyping}
        disabled={currentThread?.closed_at !== null}
      />

      {currentThread?.closed_at && (
        <div className="bg-yellow-50 border-t border-yellow-200 p-3 text-center">
          <p className="text-sm text-yellow-800">
            This chat thread has been closed
          </p>
        </div>
      )}
    </div>
  );
}

